import { entryFilesService, SignedPdf } from '@patient/services/entry/entryFilesService';
import { useState } from 'react';

import { isDefined } from '@utils/isDefined';

import type { Optional } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';

import { PopupModifiers } from '@components/Popup';

import type { EntryCdaCreationParams } from 'chaika-types';

import { useEntryPdfWithEds } from './useEntryPdfWithEds';
import { prepareSignedCda } from '../utils/prepareSignedCda';
import { prepareSignedPdf } from '../utils/prepareSignedPdf';
import { translate } from '@chaika/i18n/helper';

interface Params {
    signData(data: string | ArrayBuffer): Promise<Optional<string>>;
    onSigningFinish(): void;
}

interface Data {
    isSigning: boolean;
    signWithEDS(): void;
}

export function useSignatureUploader({ signData, onSigningFinish }: Params): Data {
    const { app, entry } = store;
    const { getEntryPdfWithEds } = useEntryPdfWithEds();

    const [isSigning, setIsSigning] = useState(false);

    function signWithEDS(): void {
        if (!isSigning) {
            setIsSigning(true);
            setTimeout(async () => {
                try {
                    const cda = await prepareSignedCda(entry.id, signData);
                    const pdf = await prepareSignedPdf(await getEntryPdfWithEds(), signData);

                    await signEntryWithEds(pdf, cda).finally(() => {
                        setIsSigning(false);
                        onSigningFinish();
                    });
                } catch (e: any) {
                    app.showPopup({
                        title: translate('edsPluginError'),
                        description: `${translate('error')}: ${e.message}`,
                    });
                }
            }, 300);
        }
    }

    async function signEntryWithEds(pdf: Optional<SignedPdf>, cda: Optional<EntryCdaCreationParams>) {
        if (isDefined(pdf)) {
            await entryFilesService.signWithEds(entry.id, pdf, cda);
        } else {
            app.showPopup({
                modifiers: [PopupModifiers.Redesign],
                title: translate('error'),
                description: translate('failedToSignEntry'),
            });
        }
    }

    return {
        isSigning,
        signWithEDS,
    };
}
